import { createContext, useContext, useState, useEffect, useRef } from 'react';

const INSTALLED_KEY = 'zahbaq_pwa_installed';

function isAlreadyInstalled() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.navigator.standalone === true) return true;   // iOS Safari
  if (localStorage.getItem(INSTALLED_KEY) === '1') return true;
  return false;
}

const PWAInstallContext = createContext({ showInstall: false, handleInstall: () => {} });

export function PWAInstallProvider({ children }) {
  const promptRef = useRef(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    // If already running as installed PWA, never show the button
    if (isAlreadyInstalled()) return;

    // Pick up event captured by index.html before React loaded
    if (window.__deferredPWAPrompt) {
      promptRef.current = window.__deferredPWAPrompt;
      setShowInstall(true);
    }

    const onBefore = (e) => {
      e.preventDefault();
      promptRef.current = e;
      window.__deferredPWAPrompt = e;
      setShowInstall(true);
    };

    const onInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, '1');
      promptRef.current = null;
      window.__deferredPWAPrompt = null;
      setShowInstall(false);
    };

    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = promptRef.current;
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    promptRef.current = null;
    window.__deferredPWAPrompt = null;
    if (outcome === 'accepted') {
      localStorage.setItem(INSTALLED_KEY, '1');
      setShowInstall(false);
    }
    // dismissed → keep button so user can try again
  };

  return (
    <PWAInstallContext.Provider value={{ showInstall, handleInstall }}>
      {children}
    </PWAInstallContext.Provider>
  );
}

export function usePWAInstall() {
  return useContext(PWAInstallContext);
}
